import { useState } from 'react'

import {
  useLazyListServicesQuery,
  useGetOneFatServiceQuery, // this one is the for initial load, which happens before the ui renders anything
  useUpsertServiceMutation,
  useBulkUpdateServicesMutation,
  useDeleteServiceMutation,
  useLazyListServiceCategoriesQuery,
  useLazyListServiceTypesQuery
} from '../../apiSlice/serviceApi'

import { useBulkUpdateContractsMutation } from '../../apiSlice/contractApi'
import { useBulkUpdateAssetsMutation } from '../../apiSlice/assetApi'

import useSearchCallback from '../../apiSlice/useSearchCallback'
import useFetchAuxiliaryLists from '../../apiSlice/useFetchAuxiliaryLists'
import { BucketConfig } from '../../../sharedcomponents/5templates/detailCards/BucketConfig'
import {
  ArtificialDetailPropertyNames,
  RootObjectTypes
} from '../../../common/globalNotary'
import { getPushPull } from '../../apiSlice/pushPullHelper'
import { useNavigate } from 'react-router-dom'

const useService = () => {
  const [loadServices] = useLazyListServicesQuery()
  const [bulkUpdateServicesCallBack] = useBulkUpdateServicesMutation()
  const [upsertServiceRecordCallback] = useUpsertServiceMutation()
  const [deleteServiceRecordCallBack] = useDeleteServiceMutation()
  const [bulkUpdateAssets] = useBulkUpdateAssetsMutation()
  const [bulkUpdateContracts] = useBulkUpdateContractsMutation()
  const [loadServiceTypes] = useLazyListServiceTypesQuery()
  const [loadServiceCategories] = useLazyListServiceCategoriesQuery()
  const [deleteService] = useDeleteServiceMutation()
  const navigate = useNavigate()

  const { searchContractsCallback, searchNonContainerAssetsCallback } =
    useSearchCallback()
  const [finalAuxiliaryRecords, setFinalAuxiliaryRecords] = useState(null)
  const [loading, setLoading] = useState(false)

  const constructAndSetFinalListBucket = (
    completeResponse,
    primaryServiceRecord
  ) => {
    const finalListBuckets = {}
    finalListBuckets.primero = []
    finalListBuckets.segundo = [
      new BucketConfig(
        completeResponse[0].data,
        searchNonContainerAssetsCallback,
        'Location',
        null
      )
    ]

    finalListBuckets.tercero = [
      new BucketConfig(
        completeResponse[1].data,
        searchContractsCallback,
        'Account',
        null
      )
    ]
    //alert(JSON.stringify(completeResponse[3].data))
    finalListBuckets.serviceTypes = completeResponse[2].data

    finalListBuckets.serviceCategories = completeResponse[3].data

    setFinalAuxiliaryRecords(finalListBuckets)
  }

  const {
    constructValueAsPromiseCall,
    constructGetAllServiceCategoriesCall,
    constructGetAllServiceTypesCall
  } = useFetchAuxiliaryLists()

  const fetchAuxiliaryRecords = async primaryServiceRecord => {
    const fetchCallArray = [
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1,
        primaryServiceRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1]
      ),
      constructValueAsPromiseCall(
        ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2,
        primaryServiceRecord[ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2]
      ),
      constructGetAllServiceTypesCall(),
      constructGetAllServiceCategoriesCall()
    ]

    /// this Promise.all will not reject, errors come back on the error object,
    /// if everything works out, the error object is null or undefined
    Promise.all(fetchCallArray.map(f => f.call())).then(res => {
      constructAndSetFinalListBucket(res, primaryServiceRecord)
    })
  }

  const listServices = async (args) => {
    return await loadServices(args)
  }

  const listServiceTypes = async () => {
    const {
      data: { data: types } = { data: [] }
    } = await loadServiceTypes()

    return types
  }

  const listServiceCategories = async () => {
    const {
      data: { data: categories } = { data: [] }
    } = await loadServiceCategories()

    return categories
  }

  const makeInMemoryServiceRecord = type => {
    return {
      name: '',
      description: 'API wants this field, yet UI does not',
      email: '',
      phone: '',
      type_id: '',
      _objectId: '',
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2]: [],
      [ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2_INITIAL_VALUES]: [],
      active: true
    }
  }

  const removeService = id => {
    deleteService({ id }).then(() => {
      navigate(-1)
    }).catch(err => {
      console.error(err)
      alert('Could not delete service. Please check that other objects do not depend on it.')
    })
  }

  const suspendService = async (id, active) => {
    bulkUpdateServicesCallBack([
      { id, suspended_at: active ? null : new Date() }
    ])
  }

  const upsertServiceCallback = primaryRecord => {
    const returnPromise = new Promise((resolve, reject) => {
      setLoading(true)

      upsertServiceRecordCallback(primaryRecord)
        .unwrap()
        .then(primaryResult => {
          const auxiliaryUpdateCallArray = []
          const id = primaryResult.data.id
            ? primaryResult.data.id
            : primaryResult.data.data.id

          const assetPushPull = getPushPull(
            primaryRecord,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY1_INITIAL_VALUES
          )

          const contractPushPull = getPushPull(
            primaryRecord,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2,
            ArtificialDetailPropertyNames.DETACHED_DEPENDENCY2_INITIAL_VALUES
          )

          if (contractPushPull.push.length || contractPushPull.pull.length)
            auxiliaryUpdateCallArray.push(
              bulkUpdateContracts([
                ...contractPushPull?.push?.map(contractId => ({
                  id: contractId,
                  service_id_assign: id
                })),
                ...contractPushPull?.pull?.map(contractId => ({
                  id: contractId,
                  service_id_unassign: id
                }))
              ])
            )
          if (assetPushPull.push.length || assetPushPull.pull.length)
            auxiliaryUpdateCallArray.push(
              bulkUpdateAssets([
                ...assetPushPull?.push?.map(assetId => ({
                  id: assetId,
                  service_id_assign: id
                })),
                ...assetPushPull?.pull?.map(assetId => ({
                  id: assetId,
                  service_id_unassign: id
                }))
              ])
            )

          if (auxiliaryUpdateCallArray.length) {
            Promise.all(auxiliaryUpdateCallArray)
              .then(results => {
                const errors = results?.flatMap(r => r.data?.errors ?? [])
                errors.length === 0
                  ? resolve({ id, type: RootObjectTypes.SERVICE })
                  : reject(
                    'The following errors have occurred: ' +
                    errors.map(e => e.msg).join(", ")
                  )
              })
            .catch(reject)
          } else {
            resolve({ id, type: RootObjectTypes.SERVICE })
          }
        })
        .catch(e => {
          reject(e)
        })
        .finally(() => {
          setLoading(false)
        })
    })

    return returnPromise
  }

  return {
    listServices,
    listServiceTypes,
    listServiceCategories,
    bulkUpdateServicesCallBack,
    useGetOneServiceQuery: useGetOneFatServiceQuery,
    fetchAuxiliaryRecords,
    makeInMemoryServiceRecord,
    finalAuxiliaryRecords,
    upsertServiceCallback,
    deleteServiceRecordCallBack,
    removeService,
    suspendService,
    loading
  }
}

export default useService
